import { memo, useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  BiChatContextType,
  BrandInsightChatContexts,
  ChatContextType,
  ChatCustomFilter,
  ChatFilterOperator,
  ChatSource,
  ChatSourcesMap,
  ProjectSubtype,
  extractBrandInsightsIds,
  getPatientJourneyIdFromChatContext,
  getProjectSubtypeIdFromChatContext,
  getSegmentFromChatContext,
  type BrandInsightsInstanceContext,
  type AgentDataSource,
  type AgentFilter,
  type ContextFilter,
  type CustomFilter,
  type ProjectFilter,
  type RecordFilter,
  type SourceFilter,
} from '@trinity/chat';
import { CapabilitiesContext } from '@trinity/chat-components/components/BrandInsights/context';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import {
  ActiveChatSessionContext,
  ChatContextItemsContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { useAgentResources } from '@trinity/chat-components/components/Chat.Header/AgentResources.Container.js';
import { HeaderContextContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ContextSearch } from '@trinity/chat-components/components/Chat.Header/ContextSearch';
import { ChatContextSearchContainer } from '@trinity/chat-components/components/Chat.Header/ContextSearch.Container';
import { SimpleContextSelector } from '@trinity/chat-components/components/Chat.Header/SimpleContextSelector';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import {
  generateEmptyChatSessionFiltersState,
  getChatSourceFilterOptions,
} from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { GroupFeaturesContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { AgentCustomFilterValueSelector } from './AgentCustomFilter.Value';
import { AgentCustomFilters } from './AgentCustomFilters';
import { isSubjectScopedContext, resolveContextFilterValue } from './common';
import { Context } from './Context';
import { DataSource } from './DataSource';
import { EntitySelect, type EntityOption } from './EntitySelect';
import { isFilterEnabledForContext } from './filterLabelMapping';
import type { AgentFilterItem } from './interfaces';
import { FilterTypeEnum } from './interfaces';
import { PendingContextContext } from './PendingContext.context';
import * as Presentation from './Presentation';
import { Project } from './Project';
import { useProjectEntityFetchers } from './ProjectEntityFetchers.context';
import { PublishedDate } from './PublishedDate';
import { Record } from './Record';
import { Source } from './Source';
import { Tag } from './Tag';

// Maps a normalised subject.kind value (lowercase) to the corresponding ChatSource enum.
// Used by SidePanelSourceController to lock the Record Type filter when viewing a specific record.
const KIND_TO_CHAT_SOURCE: Partial<Record<string, ChatSource>> = {
  transcript: ChatSource.Transcripts,
  transcripts: ChatSource.Transcripts,
  presentation: ChatSource.Presentations,
  presentations: ChatSource.Presentations,
  survey: ChatSource.Surveys,
  surveys: ChatSource.Surveys,
  survey_analysis: ChatSource.SurveyAnalyses,
  survey_analyses: ChatSource.SurveyAnalyses,
  dashboard: ChatSource.Dashboards,
  dashboards: ChatSource.Dashboards,
  text: ChatSource.Text,
  profile: ChatSource.Profiles,
  profiles: ChatSource.Profiles,
};

type Props = {
  active: boolean;
  isSidePanel?: boolean;
};

export function FilterPane(props: Props) {
  const agentResources = useAgentResources();
  const { isDigitalTwin, interactionMode } = useAvatarPanel();
  const isDTVideo = isDigitalTwin && interactionMode === 'video';
  const { isSidePanel } = props;

  // A Context pick awaiting a subject before it commits (see ContextFilterController/
  // ProjectController). Not dispatched, so an abandoned pick just evaporates on unmount.
  const [pendingContext, setPendingContext] = useState<BrandInsightsInstanceContext.ItemWithMetadata | null>(
    null
  );

  if (agentResources.isLoading) {
    return null;
  }

  return (
    <PendingContextContext.Provider value={{ pendingContext, setPendingContext }}>
      <Root isSidePanel={isSidePanel}>
        {!isSidePanel && (
          <Header>
            <HeaderLabel>Source Filters</HeaderLabel>
            <ResetController {...props} />
          </Header>
        )}
        {isDTVideo && <DTContextSection />}
        <Filters>
          {!isSidePanel && <DataSourceController dataSources={agentResources.data.dataSources} />}
          <PublishedDateController />
          <ContextFilterController pendingContext={pendingContext} setPendingContext={setPendingContext} />
          <ProjectController
            pendingContext={pendingContext}
            setPendingContext={setPendingContext}
            isSidePanel={isSidePanel}
          />
          {!isSidePanel && <RecordController />}
          {isSidePanel ? <SidePanelSourceController /> : <SourceController />}
          <TagController isSidePanel={isSidePanel} />
          <ProjectEntityController isSidePanel={isSidePanel} />

          {!isSidePanel && (
            <>
              <UserTerritoriesController filters={agentResources.data.filters} />
              <AgentCustomFiltersController filters={agentResources.data.filters} />
            </>
          )}
        </Filters>
      </Root>
    </PendingContextContext.Provider>
  );
}

function DTContextSection() {
  const headerCtx = useContext(HeaderContextContext);
  const hasActiveSearch = !!headerCtx?.pendingContextType || !!headerCtx?.pendingEverythingSearch;

  return (
    <DTSection>
      <DTSectionLabel>Context</DTSectionLabel>
      <DTSectionContent>
        <SimpleContextSelector />
        <ChatContextSearchContainer>
          {hasActiveSearch ? <StyledContextSearch /> : null}
        </ChatContextSearchContainer>
      </DTSectionContent>
    </DTSection>
  );
}

type PendingContextProps = {
  pendingContext: BrandInsightsInstanceContext.ItemWithMetadata | null;
  setPendingContext: (context: BrandInsightsInstanceContext.ItemWithMetadata | null) => void;
};

function ProjectController({
  pendingContext,
  setPendingContext,
  isSidePanel,
}: PendingContextProps & { isSidePanel?: boolean }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context: activeContext } = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  // A pending pick takes precedence so this field's mode (see Project.tsx's resolveMode)
  // matches what the user just selected rather than the still-active context.
  const context = pendingContext ?? activeContext;

  const enabled = useMemo(() => isFilterEnabledForContext(context), [context]);

  const value = useMemo(
    () => resolveSelectedProjects(context, filters.projects),
    [context, filters.projects]
  );

  // Document/Folder/Brand/Transcript contexts repurpose this field into a record search
  // (see Project.tsx's resolveMode); `filters.records` is only populated by RecordController
  // historically, so it can be undefined here.
  const records = filters.records ?? [];

  const handleRecordsChange = useCallback(
    (records: RecordFilter.Item[]) => {
      dispatch({
        type: 'filters/updated',
        payload: {
          filters: {
            records,
          },
        },
      });
    },
    [dispatch]
  );

  // Agent-context chats are scoped by `subject`, not `filters.records` — the query pipeline
  // only reads `context.data.subject`, so a selection here must update the active context.
  // This also commits a pending Context pick; clearing it just abandons it (no dispatch).
  const handleSubjectSelected = useCallback(
    (subject: { id: string; name: string } | null) => {
      if (context.type !== ChatContextType.Agent) {
        return;
      }

      // Clearing never wipes the committed subject: the chat query is scoped by it, so a
      // subject-less Agent context errors on the next question. Keep the previous subject
      // (the field's cleared display is local to Project and resets when the pane closes).
      if (subject === null) {
        if (pendingContext) {
          setPendingContext(null);
        }
        return;
      }

      dispatch({
        type: 'instance-context-changed',
        payload: {
          context: {
            type: ChatContextType.Agent,
            data: {
              ...context.data,
              subject: subject ?? undefined,
            },
            // The subtype and segment / journey scope belong to one project: moving to another
            // project must not carry them over (they would mislabel it and be sent for it).
            metadata:
              String(subject.id) === String(context.data.subject?.id)
                ? (context.metadata ?? {})
                : withoutProjectScope(context.metadata ?? {}),
          },
          brandIdentifier,
        },
      });
      setPendingContext(null);
    },
    [context, pendingContext, setPendingContext, dispatch, brandIdentifier]
  );

  return (
    <Project
      disabled={!enabled}
      isSidePanel={isSidePanel}
      context={context}
      value={value}
      onChange={(projects) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              projects,
            },
          },
        });
      }}
      records={records}
      onRecordsChange={handleRecordsChange}
      onSubjectSelected={handleSubjectSelected}
    />
  );
}

function RecordController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);
  const hasProfilesEnabled = features.hcpProfiles;

  const handleChange = useCallback(
    (records: RecordFilter.Item[]) => {
      dispatch({
        type: 'filters/updated',
        payload: {
          filters: {
            records,
          },
        },
      });
    },
    [dispatch]
  );

  if (!hasProfilesEnabled) {
    return null;
  }

  if (session.context.type !== ChatContextType.Global) {
    return null;
  }

  return (
    <Record
      context={session.context}
      projectIds={filters.projects.map((project) => project.id)}
      sourceTypes={filters.sources.map((source) => source.id)}
      onChange={handleChange}
      value={filters.records}
    />
  );
}

function AgentCustomFiltersController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  return <AgentCustomFilters context={session.context} filters={filters} />;
}

type AgentFilterValueWithMetadata = CustomFilter.ItemValue & {
  filterId?: number | string;
};

function UserTerritoriesController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);
  const sessionFilters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { agentIdentifier } = useAgentResources();
  const customFilters = useMemo(() => {
    return sessionFilters.custom || [];
  }, [sessionFilters.custom]);

  const territoryFilter = useMemo<AgentFilterItem | null>(() => {
    const foundFilter = filters?.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
    if (!foundFilter) {
      return null;
    }

    return {
      id: foundFilter.id,
      identifier: foundFilter.identifier,
      label: foundFilter.label,
      type: FilterTypeEnum.UserTerritory,
      operators: foundFilter.operators,
      data: foundFilter.data,
    };
  }, [filters]);

  const territoryFilterIndex = useMemo(() => {
    if (!territoryFilter) {
      return -1;
    }

    return customFilters.findIndex((filter) => {
      if (filter.type !== ChatCustomFilter.AgentFilter) {
        return false;
      }

      const firstValue = filter.value[0] as AgentFilterValueWithMetadata | undefined;
      if (!firstValue?.filterId) {
        return false;
      }

      return Number(firstValue.filterId) === territoryFilter.id;
    });
  }, [customFilters, territoryFilter]);

  const value = useMemo(() => {
    if (territoryFilterIndex === -1) {
      return [];
    }

    const existingFilter = customFilters[territoryFilterIndex];
    if (!existingFilter || existingFilter.type !== ChatCustomFilter.AgentFilter) {
      return [];
    }

    return existingFilter.value;
  }, [customFilters, territoryFilterIndex]);

  const handleValuesChange = useCallback(
    (values: CustomFilter.ItemValue[]) => {
      if (!territoryFilter) {
        return;
      }

      const singleValue = values[0] ? [values[0]] : [];

      if (singleValue.length === 0) {
        if (territoryFilterIndex !== -1) {
          dispatch({
            type: 'filters/remove-custom-filter',
            payload: {
              id: territoryFilterIndex,
            },
          });
        }
        return;
      }

      const territoryCustomFilter: CustomFilter.AgentCustomFilter = {
        type: ChatCustomFilter.AgentFilter,
        operator: [ChatFilterOperator.Eq],
        value: singleValue.map((item) => ({
          ...item,
          filterId: territoryFilter.id,
          filterLabel: territoryFilter.label,
        })),
      };

      if (territoryFilterIndex === -1) {
        dispatch({
          type: 'filters/add-custom-filter',
          payload: {
            value: territoryCustomFilter,
          },
        });
        return;
      }

      dispatch({
        type: 'filters/update-custom-filter',
        payload: {
          id: territoryFilterIndex,
          value: territoryCustomFilter,
        },
      });
    },
    [dispatch, territoryFilter, territoryFilterIndex]
  );

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  if (!territoryFilter || !agentIdentifier) {
    return null;
  }

  return (
    <Presentation.Row>
      <Presentation.Label>{territoryFilter.label}</Presentation.Label>
      <Presentation.Value>
        <AgentCustomFilterValueSelector
          agentIdentifier={agentIdentifier}
          filter={territoryFilter}
          values={value}
          onValuesChange={handleValuesChange}
          readOnly={false}
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}

function SourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();

  const options = useMemo(() => getChatSourceFilterOptions({ context, features }), [context, features]);

  if (!options.length) return null;

  return (
    <Source
      options={options}
      value={filters.sources}
      onChange={(sources) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              sources: sources.length ? sources : options,
            },
          },
        });
      }}
    />
  );
}

// Side-panel variant of SourceController.
// When the chat is scoped to a specific record (Transcript, or an Agent+Document subject),
// the Record Type filter is locked to the matching source type and grayed out.
function SidePanelSourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();

  const lockedSource = useMemo((): SourceFilter.Item | null => {
    if (context.type === ChatContextType.Transcript) {
      return { id: ChatSource.Transcripts, name: ChatSourcesMap.get(ChatSource.Transcripts) ?? '' };
    }

    if (context.type === ChatContextType.Agent) {
      if (context.data.identifier === BiChatContextType.Transcript) {
        return { id: ChatSource.Transcripts, name: ChatSourcesMap.get(ChatSource.Transcripts) ?? '' };
      }
      if (context.data.identifier === BiChatContextType.Document && context.data.subject?.kind) {
        const chatSource = KIND_TO_CHAT_SOURCE[context.data.subject.kind.trim().toLowerCase()];
        if (chatSource) {
          return { id: chatSource, name: ChatSourcesMap.get(chatSource) ?? '' };
        }
      }
    }

    return null;
  }, [context]);

  const options = useMemo(() => getChatSourceFilterOptions({ context, features }), [context, features]);

  if (lockedSource) {
    return <Source options={[lockedSource]} value={[lockedSource]} readOnly onChange={() => undefined} />;
  }

  if (!options.length) return null;

  return (
    <Source
      options={options}
      value={filters.sources}
      onChange={(sources) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              sources: sources.length ? sources : options,
            },
          },
        });
      }}
    />
  );
}

function PublishedDateController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const groupFeatures = useContext(GroupFeaturesContext);

  const defaultValue = useMemo(
    () =>
      generateEmptyChatSessionFiltersState({
        context,
        features: groupFeatures,
      }).minDocumentDate,
    [context, groupFeatures]
  );

  return (
    <PublishedDate
      defaultValue={defaultValue}
      value={filters.minDocumentDate}
      onChange={(minDocumentDate) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              minDocumentDate,
            },
          },
        });
      }}
    />
  );
}

function ContextFilterController({ pendingContext, setPendingContext }: PendingContextProps) {
  const dispatch = useContext(ChatStateDispatchContext);
  const contextItems = useContext(ChatContextItemsContext);
  const { context: activeContext } = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleChange = useCallback(
    (selectedValue: ContextFilter.Value) => {
      // Clearing (the select's "x" button) only ever needs to abandon an in-progress,
      // not-yet-committed pick — there's no "no context" state for an active chat to fall
      // back to, so this just reverts the display back to the real active context.
      if (!selectedValue) {
        setPendingContext(null);
        return;
      }

      if (contextItems && brandIdentifier) {
        const fullContextItem = contextItems.find((item) => item.identifier === selectedValue.identifier);
        if (fullContextItem) {
          const contextWithMetadata: BrandInsightsInstanceContext.ItemWithMetadata = {
            type: ChatContextType.Agent,
            data: fullContextItem,
            metadata: {},
          };

          // Subject-scoped kinds need a subject before this selection means anything —
          // hold it as pending until ProjectController picks one. Everything else commits now.
          if (isSubjectScopedContext(contextWithMetadata)) {
            setPendingContext(contextWithMetadata);
            return;
          }

          // Updates the active context and resets all filters in one state update.
          setPendingContext(null);
          dispatch({
            type: 'instance-context-changed',
            payload: {
              context: contextWithMetadata,
              brandIdentifier,
            },
          });
        }
      }
    },
    [dispatch, contextItems, brandIdentifier, setPendingContext]
  );

  if (!contextItems || contextItems.length === 0) {
    return null;
  }

  // Pending pick (not yet committed) takes precedence, then the active session context.
  const displayValue =
    (pendingContext?.type === ChatContextType.Agent
      ? { identifier: pendingContext.data.identifier, name: pendingContext.data.name }
      : null) ??
    (activeContext ? resolveContextFilterValue(activeContext, contextItems) : null);

  return <Context value={displayValue} onChange={(context) => handleChange(context)} />;
}

function TagController({ isSidePanel }: { isSidePanel?: boolean }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  return (
    <Tag
      context={context}
      value={filters.tags}
      isSidePanel={isSidePanel}
      onChange={(tags) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags,
            },
          },
        });
      }}
    />
  );
}

/**
 * The project's subtype alone decides which entity filter shows: Segmentation (9) gets the Segment
 * dropdown, Patient Journey (6) gets the Patient Journey dropdown. The subtype comes from the page's
 * chat context (`metadata.projectSubtypeId`), so this works the same in every app and on every page.
 */
function ProjectEntityController({ isSidePanel }: { isSidePanel?: boolean }) {
  const { context } = useContext(ActiveChatSessionContext);
  const capabilities = useContext(CapabilitiesContext);
  const { projectId } = extractBrandInsightsIds(context);
  const subtypeId = getProjectSubtypeIdFromChatContext(context);

  if (projectId === undefined) {
    return null;
  }

  // An app that doesn't say (the legacy frontend) leaves both allowed. Without the matching view
  // action the options request would be refused, so the control isn't rendered and nothing is fetched.
  if (subtypeId === ProjectSubtype.Segmentation && capabilities.canViewSegments !== false) {
    return <SegmentController projectId={projectId} isSidePanel={isSidePanel} />;
  }

  if (subtypeId === ProjectSubtype.PatientJourney && capabilities.canViewPatientJourneys !== false) {
    return <PatientJourneyController projectId={projectId} isSidePanel={isSidePanel} />;
  }

  return null;
}

const ENTITY_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

type ProjectEntityControllerProps = {
  projectId: number;
  isSidePanel?: boolean;
};

function SegmentController({ projectId, isSidePanel }: ProjectEntityControllerProps) {
  const fetchers = useProjectEntityFetchers();
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  // "Chat with Segment" fixes the chat's segment, same as it locks the Tags filter.
  const { segmentChatStatus } = useContext(SegmentChatContext);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the fetchers' identity isn't part of the cache key
  const segmentsQuery = useQuery({
    queryKey: ['get:chat/filters/segments', projectId],
    queryFn: () => fetchers.fetchSegments(projectId),
    staleTime: ENTITY_OPTIONS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const options = useMemo<EntityOption[]>(
    () =>
      (segmentsQuery.data ?? [])
        .filter((segment) => !!segment.identifier)
        .map((segment) => ({ value: segment.identifier, label: segment.name })),
    [segmentsQuery.data]
  );

  const selectedSegment = getSegmentFromChatContext(context);
  const value = useMemo<EntityOption | null>(() => {
    if (!selectedSegment) return null;
    // The context carries the name too, so the selection still shows before the options load.
    return options.find((o) => o.value === selectedSegment.id) ?? {
      value: selectedSegment.id,
      label: selectedSegment.name,
    };
  }, [options, selectedSegment]);

  const handleChange = useCallback(
    (option: EntityOption | null) => {
      dispatch({
        type: 'chat-instance-segment-updated',
        payload: { segment: option ? { id: option.value, name: option.label } : undefined },
      });
    },
    [dispatch]
  );

  return (
    <EntitySelect
      label="Segment"
      placeholder="Select a segment..."
      options={options}
      value={value}
      isLoading={segmentsQuery.isFetching}
      isError={segmentsQuery.isError}
      disabled={segmentChatStatus}
      isSidePanel={isSidePanel}
      onChange={handleChange}
    />
  );
}

function PatientJourneyController({ projectId, isSidePanel }: ProjectEntityControllerProps) {
  const fetchers = useProjectEntityFetchers();
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the fetchers' identity isn't part of the cache key
  const journeysQuery = useQuery({
    queryKey: ['get:chat/filters/patient-journeys', projectId],
    queryFn: () => fetchers.fetchPatientJourneys(projectId),
    staleTime: ENTITY_OPTIONS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  // Published journeys only: the chat's agent ignores a draft, so offering one would show a selection
  // that does nothing.
  const options = useMemo<EntityOption[]>(
    () =>
      (journeysQuery.data ?? [])
        .filter((journey) => journey.publishState === 'published')
        .map((journey) => ({ value: String(journey.id), label: journey.title })),
    [journeysQuery.data]
  );

  const selectedJourneyId = getPatientJourneyIdFromChatContext(context);
  const value = useMemo<EntityOption | null>(() => {
    if (selectedJourneyId === undefined) return null;

    const selected = options.find((o) => o.value === String(selectedJourneyId));
    if (selected) return selected;

    // The options loaded and it isn't one of them (a draft, or a journey this user can't use): the
    // chat ignores it, so show nothing selected rather than a selection that doesn't apply.
    if (journeysQuery.isSuccess) return null;

    // The context only carries the id, so use a neutral label until the options load.
    return { value: String(selectedJourneyId), label: 'Patient Journey' };
  }, [options, selectedJourneyId, journeysQuery.isSuccess]);

  const handleChange = useCallback(
    (option: EntityOption | null) => {
      dispatch({
        type: 'chat-instance-patient-journey-updated',
        payload: { patientJourneyId: option ? Number(option.value) : undefined },
      });
    },
    [dispatch]
  );

  return (
    <EntitySelect
      label="Patient Journey"
      placeholder="Select a patient journey..."
      options={options}
      value={value}
      isLoading={journeysQuery.isFetching}
      isError={journeysQuery.isError}
      isSidePanel={isSidePanel}
      onChange={handleChange}
    />
  );
}

function DataSourceController({ dataSources }: { dataSources?: AgentDataSource[] }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  const shouldRenderFilter = useMemo(() => {
    if (context.type === ChatContextType.Agent) {
      return !BrandInsightChatContexts.includes(context.data.identifier);
    }
    // Subject-scoped contexts behave like the brand-insights Agent contexts: no data source filter.
    return !isSubjectScopedContext(context);
  }, [context]);

  const transformedDataSources = useMemo(() => {
    if (!dataSources) return undefined;
    return dataSources.map((ds) => ({
      id: String(ds.id),
      name: ds.name,
    }));
  }, [dataSources]);

  const options = transformedDataSources || filters.dataSources;

  if (!shouldRenderFilter) {
    return null;
  }

  return (
    <DataSource
      context={context}
      options={options}
      value={filters.dataSources}
      dataSources={dataSources}
      onChange={(values) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              dataSources: values.length ? values : options,
            },
          },
        });
      }}
    />
  );
}

const ResetController = memo(({ active }: Props) => {
  const dispatch = useContext(ChatStateDispatchContext);
  // During "Chat with Segment" the filters are locked, so reset would only land back in the same state.
  const { segmentChatStatus } = useContext(SegmentChatContext);

  return (
    <HeaderReset disabled={!active || segmentChatStatus} onClick={() => dispatch({ type: 'filters/reset' })}>
      Reset
    </HeaderReset>
  );
});

const Root = styled('div')<{ isSidePanel?: boolean }>(({ theme, isSidePanel }) =>
  isSidePanel
    ? {
        backgroundColor: theme.palette.white.main,
        boxSizing: 'border-box',
        padding: '12px 0',
        width: '100%',
      }
    : {
        backgroundColor: theme.palette.white.main,
        borderRadius: 5,
        border: `1px solid ${theme.palette.gray.light1}`,
        boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
        boxSizing: 'border-box',
        padding: 12,
        minWidth: 450,
        maxWidth: 600,
      }
);

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const HeaderReset = styled.button(({ disabled, theme }) => ({
  color: !disabled ? theme.palette.text.primary : theme.palette.gray.light1,
  fontSize: '14px',
  transition: 'text-decoration 150ms, color 150ms',

  ':hover': {
    textDecoration: disabled ? 'underline' : 'none',
  },
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const DTSection = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: '10px 2px',
  marginBottom: '10px',
}));

const DTSectionLabel = styled.div(({ theme }) => ({
  fontSize: '12px',
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.main,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
}));

const DTSectionContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const StyledContextSearch = styled(ContextSearch)({});

/** Drops the metadata that only makes sense for the project it was registered for. */
function withoutProjectScope(metadata: Record<string, unknown>): Record<string, unknown> {
  const { projectSubtypeId: _subtype, segment: _segment, patientJourneyId: _journey, ...rest } = metadata;
  return rest;
}

function resolveSelectedProjects(
  context: BrandInsightsInstanceContext.ItemWithMetadata,
  filtersProjects: ProjectFilter.Item[]
): ProjectFilter.Item[] {
  if (context.type === ChatContextType.Project) {
    return [{ id: context.data.projectId, name: context.metadata.name }];
  }

  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Project) {
    // Defensive guard: context.data.subject may be undefined in certain staging data states
    if (context.data.subject?.id && context.data.subject?.name) {
      return [{ id: context.data.subject.id as number, name: context.data.subject.name }];
    }
  }

  return filtersProjects;
}
